import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  RemovePlayerEmailSuppressionsMutation,
  RemovePlayerEmailSuppressionsMutationVariables,
} from './__generated__/PlayerRemoveEmailSuppressionsForm';

const query = graphql`
  query SanityPlayerRemoveEmailSuppressionsForm {
    sanityPlayerRemoveEmailSuppressionsForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const mutation = gql`
  mutation RemovePlayerEmailSuppressions($playerId: ID!) {
    removeSendgridEmailSuppressions(playerId: $playerId) {
      id
      suppressionGroups {
        id
        name
      }
    }
  }
`;

const PlayerRemoveEmailSuppressionsForm: FC<{ playerId: string }> = ({
  playerId,
}) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerRemoveEmailSuppressionsFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerRemoveEmailSuppressionsForm;

  const [removePlayerEmailSuppressionsState, removePlayerEmailSuppressions] =
    useMutation<
      RemovePlayerEmailSuppressionsMutation,
      RemovePlayerEmailSuppressionsMutationVariables
    >(mutation);

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = () => {
    removePlayerEmailSuppressions({
      playerId,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  return (
    <Card
      size="lg"
      title={t(form?.title) || 'Remove Email Suppressions'}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex flex-col gap-4 p-3">
        <Form
          defaultValues={{}}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
          description="This action will remove SendGrid email suppressions from all groups"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value="Remove Suppressions"
            disabled={removePlayerEmailSuppressionsState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerRemoveEmailSuppressionsForm;
